import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, RefreshCw, AlertTriangle, BarChart3, Building2, Users, Settings, FileText, ScrollText } from 'lucide-react';

import { useAuth } from '@/App';
import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { ENTITY_TYPES } from '@/constants/entityTypes';
import { useAdminStats } from '@/hooks/admin/useAdminStats';
import { useAdminEntities } from '@/hooks/admin/useAdminEntities';
import { useAdminUsers } from '@/hooks/admin/useAdminUsers';
import { useAdminBrand } from '@/hooks/admin/useAdminBrand';
import { useAdminBulkUpload } from '@/hooks/admin/useAdminBulkUpload';
import { useAdminEntityAdmins } from '@/hooks/admin/useAdminEntityAdmins';

import BulkUploadModal from '@/pages/admin/BulkUploadModal';
import UserRoleModal from '@/pages/admin/UserRoleModal';
import EntityModal from '@/pages/admin/EntityModal';
import EntityAdminsModal from '@/pages/admin/EntityAdminsModal';
import UserEntitiesModal from '@/pages/admin/UserEntitiesModal';
import BrandSettingsTab from '@/pages/admin/BrandSettingsTab';
import ContentTab from '@/pages/admin/ContentTab';
import DashboardTab from '@/pages/admin/DashboardTab';
import EntitiesTab from '@/pages/admin/EntitiesTab';
import UsersTab from '@/pages/admin/UsersTab';
import AuditTab from '@/pages/admin/AuditTab';
import AddUserDialog from '@/pages/admin/AddUserDialog';

const AccessDenied = () => (
  <div className="min-h-screen bg-background">
    <Navigation />
    <div className="pt-20 flex items-center justify-center">
      <div className="text-center">
        <AlertTriangle className="h-16 w-16 text-destructive mx-auto mb-4" />
        <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
        <p className="text-muted-foreground">Admin privileges required</p>
      </div>
    </div>
  </div>
);

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');

  const isAdmin = user?.is_admin;
  const isSuperAdmin = user?.admin_role === 'super_admin';

  // Redirect non-admins as soon as auth check resolves
  useEffect(() => {
    if (authLoading) return;
    if (!user) return; // ProtectedRoute handles /login redirect
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [authLoading, user, isAdmin, navigate]);

  // -- Data hooks (composable orchestrator pattern) --
  const { stats, refetchStats } = useAdminStats({
    token,
    enabled: isAdmin && !authLoading,
    onForbidden: () => navigate('/dashboard'),
  });

  const entitiesH = useAdminEntities({ token, activeTab, isAdmin, user, refetchStats });
  const usersH = useAdminUsers({
    token,
    activeTab,
    isAdmin,
    refetchEntities: entitiesH.fetchEntities,
    refetchStats,
  });
  const brandH = useAdminBrand({ token, activeTab, isAdmin });
  const bulkH = useAdminBulkUpload({
    token,
    refetchEntities: entitiesH.fetchEntities,
    refetchStats,
  });
  const entityAdminsH = useAdminEntityAdmins({
    token,
    refetchEntities: entitiesH.fetchEntities,
  });

  if (!isAdmin) return <AccessDenied />;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-panel">
      <Navigation />

      <main className="px-4 pt-[68px] pb-8 sm:px-6 sm:pt-20 sm:pb-12 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 sm:mb-8 flex items-start sm:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shrink-0">
                <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold leading-tight truncate">Admin Panel</h1>
                <p className="text-xs sm:text-sm text-muted-foreground truncate">
                  {isSuperAdmin ? 'Super Admin' : 'Moderator'} Dashboard
                </p>
              </div>
            </div>

            <div className="flex gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  refetchStats();
                  entitiesH.fetchEntities();
                }}
                data-testid="refresh-btn"
              >
                <RefreshCw className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-5 sm:space-y-6">
            <TabsList className="-mx-4 px-4 sm:mx-0 sm:px-0 w-[calc(100%+2rem)] sm:w-full sm:max-w-3xl flex sm:grid sm:grid-cols-5 overflow-x-auto justify-start sm:justify-stretch [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <TabsTrigger value="dashboard" className="gap-2 whitespace-nowrap shrink-0 sm:shrink">
                <BarChart3 className="h-4 w-4" />
                Dashboard
              </TabsTrigger>
              <TabsTrigger value="entities" className="gap-2 whitespace-nowrap shrink-0 sm:shrink">
                <Building2 className="h-4 w-4" />
                Partners
              </TabsTrigger>
              <TabsTrigger value="users" className="gap-2 whitespace-nowrap shrink-0 sm:shrink">
                <Users className="h-4 w-4" />
                Users
              </TabsTrigger>
              {isSuperAdmin && (
                <TabsTrigger value="content" className="gap-2 whitespace-nowrap shrink-0 sm:shrink" data-testid="content-tab-trigger">
                  <FileText className="h-4 w-4" />
                  Content
                </TabsTrigger>
              )}
              {isSuperAdmin && (
                <TabsTrigger value="brand" className="gap-2 whitespace-nowrap shrink-0 sm:shrink">
                  <Settings className="h-4 w-4" />
                  Brand
                </TabsTrigger>
              )}
              {isSuperAdmin && (
                <TabsTrigger value="audit" className="gap-2 whitespace-nowrap shrink-0 sm:shrink" data-testid="audit-tab-trigger">
                  <ScrollText className="h-4 w-4" />
                  Audit
                </TabsTrigger>
              )}
            </TabsList>

            {/* Dashboard Tab */}
            <DashboardTab stats={stats} />

            {/* Entities Tab */}
            <EntitiesTab
              entities={entitiesH.entities}
              loading={entitiesH.loading}
              stats={stats}
              user={user}
              pagination={entitiesH.pagination}
              setPagination={entitiesH.setPagination}
              searchQuery={entitiesH.searchQuery}
              setSearchQuery={entitiesH.setSearchQuery}
              filterType={entitiesH.filterType}
              setFilterType={entitiesH.setFilterType}
              filterVerified={entitiesH.filterVerified}
              setFilterVerified={entitiesH.setFilterVerified}
              selectedEntityIds={entitiesH.selectedEntityIds}
              setSelectedEntityIds={entitiesH.setSelectedEntityIds}
              toggleEntitySelection={entitiesH.toggleEntitySelection}
              toggleSelectAllEntities={entitiesH.toggleSelectAllEntities}
              bulkDeleting={entitiesH.bulkDeleting}
              backfilling={bulkH.backfilling}
              backfillProgress={bulkH.backfillProgress}
              runBackfillLoop={() => bulkH.runBackfillLoop(stats?.entities?.geocode_pending || 0)}
              stopBackfill={bulkH.stopBackfill}
              handleBackfillCoords={() => bulkH.handleBackfillCoords(stats?.entities?.geocode_pending || 0)}
              fetchEntities={entitiesH.fetchEntities}
              handleExportEntities={entitiesH.handleExportEntities}
              setShowBulkUploadModal={bulkH.setShowBulkUploadModal}
              resetEntityForm={entitiesH.resetEntityForm}
              setShowEntityModal={entitiesH.setShowEntityModal}
              handleBulkDelete={entitiesH.handleBulkDelete}
              openEntityAdminsModal={entityAdminsH.openEntityAdminsModal}
              navigate={navigate}
              openEditModal={entitiesH.openEditModal}
              handleVerifyEntity={entitiesH.handleVerifyEntity}
              handleToggleFeatured={entitiesH.handleToggleFeatured}
              handleBulkSetFeatured={entitiesH.handleBulkSetFeatured}
              handleDeleteEntity={entitiesH.handleDeleteEntity}
              handlePrewarmScreenshots={entitiesH.handlePrewarmScreenshots}
              currentUser={user}
            />

            {/* Users Tab */}
            <UsersTab
              users={usersH.users}
              currentUser={user}
              onOpenUserEntitiesModal={usersH.openUserEntitiesModal}
              onOpenUserRoleModal={usersH.openUserRoleModal}
              onDeleteUser={usersH.handleDeleteUser}
              onBulkDeleteUsers={usersH.handleBulkDeleteUsers}
              onOpenAddUserDialog={() => usersH.setShowAddUserDialog(true)}
            />

            {/* Content Tab (Landing page copy) */}
            {isSuperAdmin && (
              <ContentTab token={token} />
            )}

            {/* Brand Settings Tab */}
            <BrandSettingsTab
              themeSettings={brandH.themeSettings}
              uploadingBrand={brandH.uploadingBrand}
              onUploadImage={brandH.handleBrandImageUpload}
              onResetTheme={brandH.fetchThemeSettings}
              onSaveTheme={brandH.handleSaveThemeSettings}
              token={token}
            />

            {/* Audit Log Tab (super admin) */}
            {isSuperAdmin && <AuditTab token={token} />}
          </Tabs>
        </div>
      </main>

      {/* Create/Edit Entity Modal */}
      <EntityModal
        open={entitiesH.showEntityModal}
        onOpenChange={entitiesH.setShowEntityModal}
        editingEntity={entitiesH.editingEntity}
        entityForm={entitiesH.entityForm}
        setEntityForm={entitiesH.setEntityForm}
        entityTypes={ENTITY_TYPES}
        existingBusinessGroups={
          // Feed the datalist with the unique, sorted set of business_group
          // values already in use. Native <datalist> auto-suggests as the
          // admin types, without needing a backend endpoint.
          Array.from(
            new Set(
              (entitiesH.entities || [])
                .map((e) => (e.business_group || '').trim())
                .filter(Boolean),
            ),
          ).sort()
        }
        galleryImages={entitiesH.galleryImages}
        profileImagePreview={entitiesH.profileImagePreview}
        uploadingFile={entitiesH.uploadingFile}
        onFileUpload={entitiesH.handleFileUpload}
        onGalleryUploadMultiple={entitiesH.handleGalleryUploadMultiple}
        onRemoveGalleryImage={entitiesH.handleRemoveGalleryImage}
        onReorderGalleryImages={entitiesH.handleReorderGalleryImages}
        onSave={entitiesH.handleCreateEntity}
      />

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        open={bulkH.showBulkUploadModal}
        onOpenChange={bulkH.setShowBulkUploadModal}
        bulkUploadMode={bulkH.bulkUploadMode}
        setBulkUploadMode={bulkH.setBulkUploadMode}
        csvFile={bulkH.csvFile}
        setCsvFile={bulkH.setCsvFile}
        bulkData={bulkH.bulkData}
        setBulkData={bulkH.setBulkData}
        bulkUploading={bulkH.bulkUploading}
        onUploadCSV={bulkH.handleBulkUploadCSV}
        onUploadJSON={bulkH.handleBulkUploadJSON}
        onDownloadCSVTemplate={bulkH.downloadCSVTemplate}
        onDownloadJSONTemplate={bulkH.downloadJSONTemplate}
      />

      {/* User Role Management Modal */}
      <UserRoleModal
        open={usersH.showUserRoleModal}
        onOpenChange={usersH.setShowUserRoleModal}
        editingUser={usersH.editingUser}
        onUpdateUserRole={usersH.handleUpdateUserRole}
      />

      {/* Entity Admins Management Modal */}
      <EntityAdminsModal
        open={entityAdminsH.showEntityAdminsModal}
        onOpenChange={entityAdminsH.setShowEntityAdminsModal}
        selectedEntity={entityAdminsH.selectedEntity}
        users={usersH.users}
        selectedEntityAdmins={entityAdminsH.selectedEntityAdmins}
        entityAdminSearch={entityAdminsH.entityAdminSearch}
        setEntityAdminSearch={entityAdminsH.setEntityAdminSearch}
        onToggleEntityAdmin={entityAdminsH.toggleEntityAdmin}
        onAssign={entityAdminsH.handleAssignEntityAdmins}
      />

      {/* User Entities Management Modal */}
      <UserEntitiesModal
        open={usersH.showUserEntitiesModal}
        onOpenChange={(open) => {
          if (!open) usersH.closeUserEntitiesModal();
        }}
        selectedUser={usersH.selectedUser}
        allEntities={usersH.allEntities}
        selectedUserEntities={usersH.selectedUserEntities}
        onToggleUserEntity={usersH.toggleUserEntity}
        onSave={usersH.handleSaveUserEntities}
        onCancel={usersH.closeUserEntitiesModal}
      />

      {/* Add User Dialog — entity list is server-scoped so entity admins only
          see partners they can legally attach to. */}
      <AddUserDialog
        open={usersH.showAddUserDialog}
        onOpenChange={usersH.setShowAddUserDialog}
        token={token}
        entities={usersH.attachableEntities}
        onCreated={() => {
          usersH.fetchUsers();
          usersH.fetchAttachableEntities();
        }}
      />
    </div>
  );
};

export default AdminPanel;
